import { useCallback, useState, type FormEvent } from 'react';
import { z } from 'zod/v4';

import { useI18n } from 'context/i18n';

export function useForm<TSchema extends z.ZodObject>({ schema }: { schema: TSchema }) {
  const { te } = useI18n();
  // This hook can be used to handle form logic, such as validation and submission.
  // Currently, it does not implement any functionality but serves as a placeholder.
  const [errors, setErrors] = useState<{ [P in keyof z.core.output<TSchema>]?: string[] }>({});

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const formData = Object.fromEntries(new FormData(e.currentTarget));
      const result = z.safeParse(schema, formData);
      if (result.success === false) {
        const issues = z.flattenError(result.error).fieldErrors;
        console.error('Validation failed:', z.prettifyError(result.error));
        setErrors(issues);
        throw issues;
        // throw new Error(`Validation failed: ${summary}`);
      }
      setErrors({});
      return result.data;
    },
    [schema],
  );

  const validate = useCallback(
    (key: keyof z.input<typeof schema>, value: unknown, errorMessage?: string) => {
      if (schema instanceof z.ZodObject === true) {
        const pickedSchemaObject: any = {
          [String(key)]: true,
        };

        const pickedSchema = schema.pick(pickedSchemaObject);
        const parsed = pickedSchema.safeParse({ [key]: value });
        if (parsed.success === true) {
          return null;
        }
        const issues = z.flattenError(parsed.error).fieldErrors as Record<string, string[]>;
        if (errorMessage) {
          return errorMessage;
        }
        const keyIssues = issues[key as string];
        if (keyIssues && keyIssues.length > 0) {
          let issueString: string = '';
          for (const issue of keyIssues) {
            const isFirst = issueString.length === 0;
            if (!isFirst) {
              issueString += '. ';
            }
            if (issue) {
              issueString += `${te(issue)}`;
            }
          }
          return issueString;
        }
        return issues[key as string]?.[0] ?? `Invalid value for ${String(key)}`;
      }
      return null;
    },
    [schema, te],
  );

  return {
    handleSubmit,
    validate,
    errors,
  };
}
